import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import { Calendar } from 'lucide-react';

/**
 * Editable date field with native date picker
 * 
 * Features:
 * - Click to edit with native date picker
 * - Formatted display (e.g., "Jan 15, 2024")
 * - Min/max date constraints
 * - Escape to cancel, blur to confirm
 * - Accessibility support with aria labels
 * 
 * @example
 * <EditableDate
 *   value="2024-01-15"
 *   onChange={(date) => setDate(date)}
 *   min="2024-01-01"
 *   placeholder="Select due date"
 * />
 */
function EditableDate({
  value = '',
  onChange,
  placeholder = 'Select date',
  error,
  disabled = false,
  min,
  max,
  className = '',
  onBlur,
  ariaLabel = 'Editable date field',
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef(null);

  // Sync local value with prop changes (only when not editing)
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value);
    }
  }, [value, isEditing]);

  // Auto-focus on edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange?.(newValue);
  }, [onChange]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    onBlur?.(localValue);
  }, [localValue, onBlur]);

  const handleClick = useCallback(() => {
    if (!disabled) {
      setIsEditing(true);
    }
  }, [disabled]);

  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'Escape':
        setLocalValue(value);
        setIsEditing(false);
        break;
      case 'Enter':
        handleBlur();
        break;
      default:
        break;
    }
  }, [value, handleBlur]);

  /**
   * Format date string for display
   * Converts YYYY-MM-DD to human-readable format
   */
  const formatDateDisplay = useCallback((dateStr) => {
    if (!dateStr) return placeholder;
    try {
      const date = new Date(dateStr + 'T00:00:00');
      if (isNaN(date.getTime())) return placeholder;
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return placeholder;
    }
  }, [placeholder]);

  if (!isEditing) {
    return (
      <div
        onClick={handleClick}
        onKeyDown={(e) => e.key === 'Enter' && handleClick()}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel}
        aria-disabled={disabled}
        className={`
          p-2 rounded cursor-pointer transition-all flex items-center
          ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-blue-50 focus:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-300'}
          ${error ? 'border-2 border-red-500 bg-red-50' : 'border border-gray-300'}
          ${className}
        `}
      >
        <Calendar size={16} className="mr-2 text-gray-500 flex-shrink-0" aria-hidden="true" />
        <div className="text-sm text-gray-900">{formatDateDisplay(localValue)}</div>
        {error && <div className="text-xs text-red-600 mt-1 ml-6" role="alert">{error}</div>}
      </div>
    );
  }

  return (
    <input
      ref={inputRef}
      type="date"
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      min={min}
      max={max}
      aria-label={ariaLabel}
      aria-invalid={!!error}
      className={`
        w-full px-2 py-2 text-sm rounded border-2 border-blue-500
        focus:outline-none focus:ring-2 focus:ring-blue-200 cursor-pointer
        ${error ? 'border-red-500 bg-red-50' : 'bg-white'}
      `}
    />
  );
}

EditableDate.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  min: PropTypes.string,
  max: PropTypes.string,
  className: PropTypes.string,
  onBlur: PropTypes.func,
  ariaLabel: PropTypes.string,
};

export default memo(EditableDate);
